import React, { useEffect, useRef, useState } from 'react';
import { Box, Grow } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { emailIsValidRe, wait } from 'globals/utils';
import defaultLocales from 'globals/locales/default';
import FsSimpleInput from 'ui-elements/form-elements/FsDefaultInput';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsFormFullWidthContainer from 'ui-elements/form-elements/FsFormFullWidthContainer';
import FsPhoneNumberInput from 'ui-elements/form-elements/FsPhoneNumberInput';
import { Caption } from './CompanyForm.style-elements';

interface CompanyFormProps {
  inStatus: boolean;
  transitionTime: number;
  onClose?: () => void;
}

const CompanyForm: React.FC<CompanyFormProps> = ({
  inStatus,
  transitionTime,
  onClose,
}) => {
  const formEl = useRef<HTMLFormElement>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [companyName, setCompanyName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('+44');
  const [reference, setReference] = useState<string>('');
  const [pending, setPending] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const fields = [
    {
      required: true,
      name: 'company',
      value: companyName,
      label: defaultLocales.company,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setCompanyName(e.target?.value);
      },
    },
    {
      required: true,
      name: defaultLocales.email.toLowerCase(),
      value: email,
      label: defaultLocales.email,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setEmail(e.target?.value);
      },
    },
    {
      required: true,
      name: 'phone',
      value: phone,
      label: defaultLocales.phoneNumber,
      placeholder: '+44*',
      country: 'gb',
      onChange: (phoneNumber: string): void => {
        setPhone(phoneNumber);
      },
    },
    {
      required: true,
      name: defaultLocales.reference.toLowerCase(),
      value: reference,
      label: defaultLocales.reference,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setReference(e.target?.value);
      },
    },
  ];

  const resetForm = () => {
    setCompanyName('');
    setEmail('');
    setPhone('44');
    setReference('');
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setPending(true);
    // eslint-disable-next-line no-console
    console.log({
      companyName,
      email,
      phone,
      reference,
    });
    await wait(1000);
    setPending(false);
    if (onClose) onClose();
    enqueueSnackbar(defaultLocales.invitationSendNotificationSuccess, {
      variant: 'success',
    });
    resetForm();
  };

  useEffect(() => {
    const isDefaultFormValid = !!formEl?.current?.checkValidity();
    const isPhoneValid = phone.length >= 5;
    const isEmailValid = !!email.match(emailIsValidRe);
    const isCompanyNameValid = companyName.length >= 3;
    const isReferenceValid = reference.length >= 3;

    const isValid =
      isDefaultFormValid &&
      isPhoneValid &&
      isEmailValid &&
      isCompanyNameValid &&
      isReferenceValid;
    setIsFormValid(isValid);
  }, [companyName, email, phone, reference]);

  return (
    <Grow in={inStatus} timeout={transitionTime}>
      <Box>
        <Caption>Company Data</Caption>
        <form onSubmit={handleSubmit} ref={formEl}>
          {fields.map(({ name, onChange, ...otherProps }) => (
            <FsFormFullWidthContainer key={name}>
              {name === 'phone' ? (
                <FsPhoneNumberInput
                  {...otherProps}
                  onChange={onChange as (phoneNumber: string) => void}
                />
              ) : (
                <FsSimpleInput
                  {...{ ...otherProps, name }}
                  fsVariant="outlined"
                  onChange={
                    onChange as (e: React.ChangeEvent<HTMLInputElement>) => void
                  }
                  fullWidth
                />
              )}
            </FsFormFullWidthContainer>
          ))}
          <FsPendingButton
            type="submit"
            variant="contained"
            color="primary"
            pending={pending}
            width={160}
            disabled={!isFormValid}
          >
            {defaultLocales.invite}
          </FsPendingButton>
        </form>
      </Box>
    </Grow>
  );
};

export default CompanyForm;
